import { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { TopBar } from '@/components/TopBar';
import { AdaptiveToolbar } from '@/components/AdaptiveToolbar';
import { PreviewCanvas } from '@/components/PreviewCanvas';
import { Inspector } from '@/components/Inspector';
import { Storyboard } from '@/components/Storyboard';
import { CmdKPalette } from '@/components/CmdKPalette';
import { GenerateModal } from '@/components/GenerateModal';
import { Toast } from '@/components/Toast';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useProject } from '@/hooks/useProject';
import { useEditor } from '@/store/editor';
import { Icon } from '@/components/ui/Icon';

export function EditorPage() {
  const { projectId = 'demo' } = useParams<{ projectId: string }>();
  const setAiAnalyzing = useEditor((s) => s.setAiAnalyzing);
  useKeyboardShortcuts();

  const isDemo = projectId === 'demo';
  const { project, assets, isLoading, error } = useProject(projectId);

  useEffect(() => {
    if (!isDemo) return;
    const id = setTimeout(() => setAiAnalyzing(false), 5000);
    return () => clearTimeout(id);
  }, [setAiAnalyzing, isDemo]);

  if (!isDemo && error) {
    return <Navigate to="/projects" replace />;
  }

  if (!isDemo && isLoading) {
    return (
      <div className="h-screen flex items-center justify-center text-text-muted text-sm gap-2">
        <Icon name="loader-2" size={16} className="animate-spin" />
        Laddar projekt...
      </div>
    );
  }

  const previewAsset = assets.find((a) => a.kind === 'video') ?? assets[0];

  return (
    <div className="h-screen grid grid-rows-[52px_1fr_200px]">
      <TopBar projectName={project?.name ?? (isDemo ? 'Demo-projekt' : 'Projekt')} />
      <div className="grid grid-cols-[64px_1fr_320px] overflow-hidden">
        <AdaptiveToolbar />
        <PreviewCanvas asset={previewAsset} />
        <Inspector />
      </div>
      <Storyboard projectId={isDemo ? null : projectId} assets={assets} />
      <CmdKPalette />
      <GenerateModal />
      <Toast />
    </div>
  );
}
